import Loader from '@components/Loader'
import { ProductReflectionFeedbacksQueryResult } from '@graphql/queries/__generated__/product-details.generated'
import React, { useState } from 'react'
import { makeStyles } from '@mui/styles'
import { Button } from '@mui/material'
import CalendarIcon from '@icons/CalendarIcon'
import LeftClickIcon from '@icons/LeftClickIcon'
import MoreHorizIcon from '@icons/MoreHorizIcon'
import Rating from '@mui/material/Rating'
import { decodeHtml, hideUsername } from '../../../../utils/misc'
import FormattedDate from '@components/FormattedDate'
import { SwiperOptions } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { FeedbackBaseFragment } from '@graphql/fragments/__generated__/Feedback.generated'

const useStyles = makeStyles(theme => ({
  wrapper: {
    backgroundColor: '#eaf2ff',
    borderRadius: '8px',
    border: '1px solid #d6e6ff',
  },
  button: {
    '&:hover': {
      backgroundColor: '#d1e2ff !important',
    },
  },
}))

interface IProps {
  dataFeedbacks: ProductReflectionFeedbacksQueryResult['data']['specific']['product']['feedbacks']
  loading?: boolean

  setIsShowAll(val: boolean): void
}

const ProductFeedbacks: React.FC<IProps> = ({ dataFeedbacks, loading, setIsShowAll }) => {
  const classes = useStyles()
  const [activeFeedback, setActiveFeedback] = useState<FeedbackBaseFragment>(dataFeedbacks.results[0])

  const swiperOptions: SwiperOptions = {
    spaceBetween: 0,
    slidesPerView: 1,
    pagination: {
      clickable: true,
      el: `#swiper-pagination-feedback`,
    },
    watchSlidesProgress: false,
    loop: true,
    allowSlideNext: dataFeedbacks.results.length > 1,
    allowSlidePrev: dataFeedbacks.results.length > 1,
    navigation: {
      prevEl: `#swiper-button-prev-feedback`,
      nextEl: `#swiper-button-next-feedback`,
    },
    autoHeight: true,
  }

  return (
    <div className='my-30'>
      <Loader className='spinner' type='ThreeDots' color='#333' visible={loading} height={100} width={100} />
      <div className={classes.wrapper}>
        <div className='relative px-20'>
          <Swiper
            {...swiperOptions}
            onActiveIndexChange={({ realIndex }) => setActiveFeedback(dataFeedbacks.results[realIndex])}
          >
            {dataFeedbacks.results.map(node => {
              return (
                <SwiperSlide key={node.id}>
                  <div className='p-16 flex-1'>
                    <div className='flex items-center gap-16 pb-10'>
                      <span className='text-xl font-medium'>{hideUsername(node.userName)}</span>
                      <Rating name='read-only' value={node.score} size='large' readOnly />
                    </div>
                    <div className='text-gray text-base'>{decodeHtml(node.comment)}</div>
                  </div>
                </SwiperSlide>
              )
            })}
          </Swiper>
          <div
            id={`swiper-button-prev-feedback`}
            className='swiper-button-prev swiper-button--xs'
            style={{ color: '#000' }}
          />
          <div
            id={`swiper-button-next-feedback`}
            className='swiper-button-next swiper-button--xs'
            style={{ color: '#000' }}
          />
        </div>

        <div className='flex h-[42px]'>
          <Button
            className='flex-1'
            classes={{ root: classes.button }}
            startIcon={<CalendarIcon viewBox='0 -960 960 960' />}
            sx={{ fontWeight: 400, border: '1px solid #d6e6ff', borderLeft: 'none', borderBottom: 'none' }}
            disableRipple
            disableElevation
            fullWidth
          >
            <FormattedDate value={activeFeedback.timeCreated} />
          </Button>
          <Button
            className='flex-1'
            classes={{ root: classes.button }}
            startIcon={<LeftClickIcon viewBox='0 -960 960 960' />}
            sx={{ fontWeight: 400, border: '1px solid #d6e6ff', borderLeft: 'none', borderBottom: 'none' }}
            disabled={!activeFeedback.url}
            component='a'
            href={activeFeedback.url}
            target='_blank'
            fullWidth
          >
            Review's place
          </Button>
          <Button
            className='flex-1'
            classes={{ root: classes.button }}
            startIcon={<MoreHorizIcon viewBox='0 -960 960 960' />}
            sx={{
              fontWeight: 400,
              border: '1px solid #d6e6ff',
              borderLeft: 'none',
              borderBottom: 'none',
              borderRight: 'none',
            }}
            onClick={() => setIsShowAll(true)}
            fullWidth
          >
            More
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ProductFeedbacks
